import { AlertTriangle, ExternalLink, Search } from "lucide-react";
import { Fragment, useState } from "react";
import { Link } from "react-router";
import { useAdminSubscriptions } from "../../api/admin";
import { AdminSubscriptionsFilters } from "../../types/admin";
import { formatDate } from "../../utils/dateUtils";
import { DEFAULT_PAGE } from "./pagination";
import { SubscriptionTransfer } from "./SubscriptionTransfer";
import {
  Badge,
  Card,
  Empty,
  Loading,
  PageHeader,
  PaginationBar,
  SubscriptionBadge,
} from "./ui";
import { SEARCH_INPUT_PROPS } from "./inputs";

const ALERTS: Record<
  NonNullable<AdminSubscriptionsFilters["alert"]>,
  { label: string; help: string }
> = {
  several_per_user: {
    label: "Comptes avec plusieurs abonnements",
    help: "Un achat de plusieurs mémoirs, ou un second achat sur le même email, destiné à quelqu'un d'autre : l'acheteur peut le transmettre depuis son tableau de bord, ou réaffectez-le.",
  },
  several_books: {
    label: "Anciens achats PDF en quantité",
    help: "Commandes d'avant « un abonnement par mémoir » : séparez les livres destinés à d'autres personnes. Les exemplaires d'un livre imprimé ne comptent pas.",
  },
};

export const AdminSubscriptions = () => {
  const [filters, setFilters] = useState<AdminSubscriptionsFilters>({});
  const [page, setPage] = useState(DEFAULT_PAGE);
  const { data, isLoading } = useAdminSubscriptions({ ...filters, ...page });
  const [openId, setOpenId] = useState<number | null>(null);

  const filter = (update: Partial<AdminSubscriptionsFilters>) => {
    setFilters((f) => ({ ...f, ...update }));
    setPage((p) => ({ ...p, page: 1 }));
  };

  return (
    <>
      <PageHeader
        title="Abonnements"
        subtitle="Un abonnement par mémoir acheté : une commande de 2 mémoirs en crée 2. Un compte n'en utilise qu'un ; les autres sont à transmettre et sont signalés ici."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {(Object.keys(ALERTS) as (keyof typeof ALERTS)[]).map((key) => {
          const count = data?.alerts[key] ?? 0;
          const active = filters.alert === key;
          return (
            <button
              key={key}
              onClick={() => filter({ alert: active ? undefined : key })}
              className={`text-left rounded-lg border p-4 ${
                active
                  ? "border-indigo-500 bg-indigo-50"
                  : count > 0
                    ? "border-red-200 bg-red-50 hover:bg-red-100"
                    : "border-gray-100 bg-white hover:bg-gray-50"
              }`}
            >
              <p className="flex items-center gap-2 text-sm font-medium text-gray-900">
                {count > 0 && (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                )}
                {ALERTS[key].label} : {count}
              </p>
              <p className="text-xs text-gray-500 mt-1">{ALERTS[key].help}</p>
              {active && (
                <p className="text-xs text-indigo-600 mt-1">
                  Filtre actif — cliquer pour l'enlever
                </p>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={filters.search ?? ""}
            onChange={(e) => filter({ search: e.target.value || undefined })}
            {...SEARCH_INPUT_PROPS}
            placeholder="Email, ID ou n° de commande Shopify…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <select
          value={filters.status ?? ""}
          onChange={(e) => filter({ status: e.target.value || undefined })}
          className="text-sm border border-gray-300 rounded-md px-3 py-2"
        >
          <option value="">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="pending">En attente</option>
          <option value="cancelled">Annulé</option>
        </select>
        <select
          value={filters.product ?? ""}
          onChange={(e) => filter({ product: e.target.value || undefined })}
          className="text-sm border border-gray-300 rounded-md px-3 py-2"
        >
          <option value="">Tous les produits</option>
          <option value="pdf">PDF</option>
          <option value="print">Print</option>
        </select>
      </div>

      <Card>
        {isLoading ? (
          <div className="p-5">
            <Loading />
          </div>
        ) : !data || data.subscriptions.length === 0 ? (
          <Empty>Aucun abonnement.</Empty>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                <th className="px-5 py-3 font-medium">Titulaire</th>
                <th className="px-5 py-3 font-medium">Commande Shopify</th>
                <th className="px-5 py-3 font-medium">Produit</th>
                <th className="px-5 py-3 font-medium">Livres</th>
                <th className="px-5 py-3 font-medium">Acheté le</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 align-top">
              {data.subscriptions.map((sub) => (
                <Fragment key={sub.id}>
                  <tr>
                    <td className="px-5 py-3">
                      <Link
                        to={`/admin/users/${sub.user_id}`}
                        className="font-medium text-indigo-600 hover:underline"
                      >
                        {sub.user_email}
                      </Link>
                      <div className="flex gap-1 mt-1">
                        <SubscriptionBadge status={sub.status} />
                        {(sub.user_subscriptions_count ?? 0) > 1 && (
                          <Badge color="red">
                            {sub.user_subscriptions_count} abonnements sur ce
                            compte
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {sub.shopify_admin_url ? (
                        <a
                          href={sub.shopify_admin_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                        >
                          {sub.shopify_order_name ?? sub.shopify_order_id}
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      ) : (
                        <span className="text-gray-500">
                          {sub.shopify_order_name ?? sub.shopify_order_id}
                        </span>
                      )}
                      {sub.is_gift && (
                        <p className="text-xs text-gray-400">
                          Cadeau de {sub.offered_by_email}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Badge color="blue">
                        {sub.product === "print" ? "Print" : "PDF"}
                      </Badge>
                      {sub.with_audiobook && (
                        <Badge color="purple">Audio</Badge>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={
                          sub.books_purchased > 1
                            ? "font-semibold text-red-700"
                            : ""
                        }
                      >
                        {sub.books_purchased} acheté(s)
                      </span>
                      <p className="text-xs text-gray-400">
                        {sub.books_remaining} restant(s) · {sub.books_printed}{" "}
                        imprimé(s)
                      </p>
                    </td>
                    <td className="px-5 py-3 text-gray-500">
                      {formatDate(sub.purchased_at)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() =>
                          setOpenId((id) => (id === sub.id ? null : sub.id))
                        }
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        {openId === sub.id ? "Fermer" : "Réaffecter / séparer"}
                      </button>
                    </td>
                  </tr>
                  {openId === sub.id && (
                    <tr>
                      <td colSpan={6} className="px-5 pb-4">
                        <SubscriptionTransfer subscription={sub} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
        <PaginationBar pagination={data?.pagination} onChange={setPage} />
      </Card>
    </>
  );
};
